import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, RefreshCw, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useSubstatusBreakdown, type AvailabilityStatus } from "@/hooks/use-population-insights";
import { useInsightsContext } from "@/contexts/insights-context";
import { useState, useMemo } from "react";
import { useToggleableSeries, InteractiveLegend, type LegendSeries } from "./interactive-legend";

type SpeciesFilter = "all" | "dogs" | "cats" | "other";

const SPECIES_API_MAP: Record<SpeciesFilter, string | undefined> = {
  all: undefined,
  dogs: "DOG",
  cats: "CAT",
  other: undefined,
};

const UNAVAIL_KEYS = ["noFlags", "medical", "behavior", "behMed", "pendAdopt"] as const;
const AVAIL_KEYS = ["noFlags", "medical", "behavior", "behMed"] as const;

const SUBSTATUS_COLORS: Record<string, string> = {
  noFlags: "hsl(210 70% 50%)",
  medical: "hsl(0 72% 51%)",
  behavior: "hsl(38 92% 50%)",
  behMed: "hsl(280 65% 55%)",
  pendAdopt: "hsl(142 70% 45%)",
};

const SUBSTATUS_LABELS: Record<string, string> = {
  noFlags: "No Flags",
  medical: "Medical",
  behavior: "Behavior",
  behMed: "Beh + Med",
  pendAdopt: "Pending Adopt",
};

interface SubstatusChartProps {
  days: number;
}

function StatCard({ label, value, color, testId }: { label: string; value: number; color?: string; testId: string }) {
  return (
    <div className="flex flex-col p-3 rounded-md border" data-testid={testId}>
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-lg font-semibold" style={color ? { color } : undefined}>{value}</span>
    </div>
  );
}

export function SubstatusChart({ days }: SubstatusChartProps) {
  const { selectedShelter, isSystemView } = useInsightsContext();
  const location = isSystemView ? undefined : selectedShelter;
  const [speciesFilter, setSpeciesFilter] = useState<SpeciesFilter>("all");
  const [availStatus, setAvailStatus] = useState<AvailabilityStatus>("available");

  const apiSpecies = SPECIES_API_MAP[speciesFilter];
  const { data, isLoading, isError, refetch } = useSubstatusBreakdown(days, location, apiSpecies, availStatus);

  const activeKeys = availStatus === "unavailable" ? UNAVAIL_KEYS : AVAIL_KEYS;

  const allPieData = useMemo(() => {
    if (!data?.current) return [];
    const current = speciesFilter === "other" && data.current.bySpecies?.OTHER
      ? data.current.bySpecies.OTHER
      : data.current;
    return activeKeys
      .map((key) => ({
        name: SUBSTATUS_LABELS[key],
        key,
        value: (current as any)[key] || 0,
        color: SUBSTATUS_COLORS[key],
      }))
      .filter(d => d.value > 0);
  }, [data, speciesFilter, activeKeys]);

  const legendSeries = useMemo<LegendSeries[]>(() =>
    allPieData.map(d => ({ key: d.name, label: d.name, color: d.color })),
    [allPieData]
  );

  const { visibleSeries, toggleSeries } = useToggleableSeries(legendSeries);

  const pieData = useMemo(
    () => allPieData.filter(d => visibleSeries.has(d.name)),
    [allPieData, visibleSeries]
  );

  const currentStats = useMemo(() => {
    if (!data?.current) return null;
    if (speciesFilter === "other" && data.current.bySpecies?.OTHER) {
      return { ...data.current.bySpecies.OTHER };
    }
    return data.current;
  }, [data, speciesFilter]);

  const chartTitle = "Availability Breakdown";
  const totalLabel = availStatus === "available" ? "Total Avail." : "Total Unavail.";
  const emptyMsg = `No ${availStatus} substatus data found for the selected filters.`;

  if (isLoading) {
    return (
      <Card data-testid="card-substatus-breakdown">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5" />
            {chartTitle}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card data-testid="card-substatus-breakdown">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5" />
            {chartTitle}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center gap-2">
              Failed to load substatus data.
              <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-retry-substatus">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="card-substatus-breakdown">
      <CardHeader className="flex flex-row items-start justify-between gap-2 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <CardTitle className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5" />
            {chartTitle}
          </CardTitle>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={availStatus} onValueChange={(v) => setAvailStatus(v as AvailabilityStatus)}>
            <SelectTrigger className="w-[170px]" data-testid="select-availability-status">
              <span className="text-muted-foreground mr-1">Status:</span> <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="unavailable">Unavailable</SelectItem>
            </SelectContent>
          </Select>
          <Select value={speciesFilter} onValueChange={(v) => setSpeciesFilter(v as SpeciesFilter)}>
            <SelectTrigger className="w-[130px]" data-testid="select-substatus-species">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Species</SelectItem>
              <SelectItem value="dogs">Dogs</SelectItem>
              <SelectItem value="cats">Cats</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {currentStats && (
          <div className="mb-4" data-testid="substatus-stats">
            <p className="text-xs text-muted-foreground mb-1" data-testid="text-substatus-stats-hint">
              Current snapshot
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <StatCard
                label={totalLabel}
                value={currentStats.total || activeKeys.reduce((s, k) => s + ((currentStats as any)[k] || 0), 0)}
                testId="stat-substatus-total"
              />
              {activeKeys.map((key) => (
                <StatCard
                  key={key}
                  label={SUBSTATUS_LABELS[key]}
                  value={(currentStats as any)[key] || 0}
                  color={SUBSTATUS_COLORS[key]}
                  testId={`stat-substatus-${key.toLowerCase()}`}
                />
              ))}
            </div>
          </div>
        )}

        {allPieData.length > 0 ? (
          <>
            <InteractiveLegend
              series={legendSeries}
              visibleSeries={visibleSeries}
              onToggle={toggleSeries}
              testIdPrefix="substatus-legend"
            />
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    borderColor: "hsl(var(--border))",
                    borderRadius: "6px",
                    color: "hsl(var(--card-foreground))",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </>
        ) : (
          <div className="flex items-center justify-center h-[200px] text-muted-foreground" data-testid="text-substatus-empty">
            {emptyMsg}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
